import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Package } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useI18n } from "../i18n";

export default function CheckoutSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking, paid, failed, timeout
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          setOrder(data);
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {
        // keep trying
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-lg mx-auto px-5 py-20">
        <div className="bg-white nb-border nb-shadow-lg rounded-3xl p-10 text-center">
          {state === "checking" && (
            <>
              <Loader2 size={64} className="mx-auto mb-5 animate-spin text-nbpink" />
              <h1 className="font-head font-extrabold text-3xl mb-2">{t("success.checking")}</h1>
              <p className="text-nbblack/60">{t("success.checkingSub")}</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 size={72} className="mx-auto mb-5 text-green-500" />
              <h1 className="font-head font-extrabold text-4xl mb-2">{t("success.paidTitle")}</h1>
              <p className="text-nbblack/60 mb-8">{t("success.paidSub")}</p>
              {order?.shipping?.full_name && (
                <div className="text-left bg-[#FFF9F0] nb-border rounded-2xl p-4 mb-8" data-testid="success-shipping">
                  <p className="font-head font-bold text-xs uppercase tracking-wide text-nbblack/50 mb-1">{t("success.shippingTo")}</p>
                  <p className="font-bold">{order.shipping.full_name}</p>
                  {(order.shipping.email || order.shipping.phone) && (
                    <p className="text-sm text-nbblack/70">
                      {order.shipping.email}{order.shipping.phone ? ` · ${order.shipping.phone}` : ""}
                    </p>
                  )}
                  <p className="text-sm text-nbblack/70">
                    {order.shipping.address1}{order.shipping.address2 ? `, ${order.shipping.address2}` : ""}
                  </p>
                  <p className="text-sm text-nbblack/70">
                    {[order.shipping.city, order.shipping.province, order.shipping.postal_code].filter(Boolean).join(", ")}
                    {order.shipping.country ? ` · ${order.shipping.country}` : ""}
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/orders" data-testid="view-orders" className="inline-flex items-center justify-center gap-2 bg-nbblack text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">
                  <Package size={18} /> {t("success.viewOrders")}
                </Link>
                <Link to="/design" className="inline-flex items-center justify-center bg-nbyellow font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">
                  {t("success.designMore")}
                </Link>
              </div>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle size={72} className="mx-auto mb-5 text-red-500" />
              <h1 className="font-head font-extrabold text-3xl mb-2">
                {state === "timeout" ? t("success.timeoutTitle") : t("success.failedTitle")}
              </h1>
              <p className="text-nbblack/60 mb-8">
                {state === "timeout" ? t("success.timeoutMsg") : t("success.failedMsg")}
              </p>
              <Link to="/cart" className="inline-flex items-center justify-center bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">
                {t("success.backToCart")}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
